/**
 * Keep the current and client-side navigated documents available to Serwist.
 *
 * Caching is best-effort: navigation must not wait on a worker response.
 * Post directly instead of allocating a response-oriented MessageChannel.
 */
function cacheSerwistUrl(windowRef, url) {
  if (!windowRef?.navigator?.onLine || !url) return;

  const worker = windowRef.navigator.serviceWorker?.controller;
  if (!worker) return;

  try {
    worker.postMessage({
      type: "CACHE_URLS",
      payload: { urlsToCache: [url] },
    });
  } catch {
    // A worker can disappear between the availability check and postMessage.
  }
}

/**
 * Attach cache updates to Next Pages Router navigation and reconnect events.
 * Returns the cleanup function for use from a React effect.
 */
export function setupSerwistNavigationCache(
  router,
  windowRef = typeof window === "undefined" ? undefined : window,
) {
  if (
    !windowRef ||
    !windowRef.navigator ||
    process.env.NODE_ENV !== "production" ||
    !("serviceWorker" in windowRef.navigator)
  ) {
    return () => {};
  }

  const cacheCurrentPathname = () => {
    cacheSerwistUrl(windowRef, windowRef.location.pathname);
  };
  const cacheRoute = (url) => {
    cacheSerwistUrl(windowRef, url);
  };
  const cacheAfterControllerChange = () => {
    cacheCurrentPathname();
  };

  router.events.on("routeChangeComplete", cacheRoute);
  windowRef.addEventListener("online", cacheCurrentPathname);
  windowRef.navigator.serviceWorker.addEventListener(
    "controllerchange",
    cacheAfterControllerChange,
  );

  return () => {
    router.events.off("routeChangeComplete", cacheRoute);
    windowRef.removeEventListener("online", cacheCurrentPathname);
    windowRef.navigator.serviceWorker.removeEventListener(
      "controllerchange",
      cacheAfterControllerChange,
    );
  };
}
